/**
 * Concrete web adapters wiring `@shopista/shared/stores` Adapters interface.
 *
 * The app entry (`main.tsx`) constructs the full bag once and passes it down
 * to orchestrators. Tests construct their own adapters (usually MemoryStorage
 * + a recording fetch) and run reducers in isolation.
 */

import type { TokenProvider } from '@shopista/shared/api';
import type { Adapters } from '@shopista/shared/stores';
import { createWebApiAdapter, type WebApiAdapter } from './api';
import { createWebClockAdapter } from './clock';
import { createWebPushAdapter } from './push';
import { createWebStorageAdapter } from './storage';

export { createWebApiAdapter } from './api';
export { createWebClockAdapter } from './clock';
export { createWebPushAdapter } from './push';
export { createWebStorageAdapter } from './storage';

export interface CreateWebAdaptersOptions {
  /** Backend base URL, e.g. `http://localhost:3001`. No trailing slash. */
  apiBaseUrl: string;
  /**
   * Optional TokenProvider override. When omitted the API adapter builds its
   * own default localStorage-backed provider (see `api.ts` for the legacy
   * key names). T7's `main.tsx` supplies an explicit instance from
   * `makeTokenStorage()` so the HttpClient + auth layer share one adapter.
   */
  tokens?: TokenProvider;
}

export type WebAdapters = Adapters & { api: WebApiAdapter };

export function createWebAdapters(options: CreateWebAdaptersOptions): WebAdapters {
  return {
    storage: createWebStorageAdapter(),
    api: createWebApiAdapter(
      options.tokens
        ? { baseUrl: options.apiBaseUrl, tokens: options.tokens }
        : { baseUrl: options.apiBaseUrl }
    ),
    push: createWebPushAdapter(),
    clock: createWebClockAdapter(),
  };
}
